// @flow

import { observer } from "mobx-react";
import React from "react";
import Textarea from "react-textarea-autosize";

import TimelineItemService from "app/services/TimelineItemService";
import TextExtractor from "app/util/TextExtractor";

import TimelineItem from "app/models/TimelineItem";
import type { TimelineItemMetadata } from "app/models/TimelineItem";

import AnimatedIconLoading from "app/components/AnimatedIconLoading/AnimatedIconLoading";
import DropZone from "app/components/DropZone/DropZone";
import FileSelector from "app/components/FileSelector/FileSelector";
import WizardTimelineCardPreview from "app/components/TimelineCard/WizardTimelineCard/WizardTimelineCardPreview/WizardTimelineCardPreview";

import "./WizardTimelineCard.scss";
import MediaRecorderButton from "../../MediaRecorderButton/MediaRecorderButton";

type Props = {
    item: TimelineItem,
    onSave: Function,
    onDelete: Function,
    onCancel: Function,
    onChange: Function
};

type State = {
    comment: string,
    url?: string,
    image?: string,
    loadingPreview: boolean,
    metadata?: TimelineItemMetadata,
    file?: File,
    saving: boolean
};

/**
 * This is a special Card. Responsible for handling adding and editing items
 */
@observer
export default class WizardTimelineCard extends React.Component<
    Props,
    State
> {
    constructor(props: Props) {
        super(props);

        const item = this.props.item;

        this.state = {
            comment: item.comment || "",
            url: item.url,
            image: item.image,
            loadingPreview: false,
            saving: false,
            metadata: !item.metaImage
                ? undefined
                : {
                      title: item.metaTitle,
                      description: item.metaDescription,
                      image: item.metaImage.src,
                      video:
                          item.metadata && item.metadata.video
                              ? item.metadata.video
                              : undefined
                  }
        };
    }

    componentWillUpdate() {
        this.props.onChange();
    }

    getPreviewType() {
        return WizardTimelineCardPreview.getType(
            this.state.loadingPreview,
            this.state.metadata,
            this.state.image,
            this.state.file
        );
    }

    handleChange = (event: SyntheticInputEvent<HTMLTextAreaElement>) => {
        this.setState({ comment: event.target.value });
    };

    handlePaste = (event: SyntheticClipboardEvent<HTMLTextAreaElement>) => {
        if (this.getPreviewType()) return;

        const url = TextExtractor.firstUrl(
            event.clipboardData.getData("text/plain")
        );
        if (url && url !== this.state.url) {
            this.setState({ loadingPreview: true });

            TimelineItemService.metadata(url).then(
                ({ data }) => {
                    this.setState({
                        loadingPreview: false,
                        url: data.url,
                        metadata: {
                            title: data.title,
                            image: data.image,
                            description: data.description
                        }
                    });
                },
                ({ response }: any) => {
                    this.setState({ loadingPreview: false });
                    this.handleRemovePreview();
                }
            );
        }
    };

    handleRemovePreview = () => {
        this.setState({
            url: undefined,
            metadata: undefined,
            image: undefined,
            file: undefined
        });
    };

    handleSelectFile = (event: SyntheticInputEvent<HTMLInputElement>) => {
        this.setState({ file: event.target.files[0] });
    };

    handleVideoRecorded = (video: File) => {
        this.setState({ file: video });
    };

    handleDropFile = (event: any) => {
        const files = event.dataTransfer.files;
        if (files.length) {
            this.setState({ file: files[0] });
        }
    };

    handleSubmit = (event: Event) => {
        event.preventDefault();

        const state = this.state;
        this.setState({ saving: true });
        this.props
            .onSave(
                state.comment,
                state.url,
                state.image,
                state.metadata,
                state.file
            )
            .catch(() => {
                this.setState({ saving: false });
            });
    };

    render() {
        const item = this.props.item;
        const adding = !item.rowid;
        const uploadDisabled = !!this.getPreviewType();
        const isTextRequired =
            !this.state.file && !this.state.image && !this.state.url;
        const commentLength = 500;

        let classes = ["WizardTimelineCard"];
        if (this.state.image)
            classes.push("WizardTimelineCard__content--image");

        return (
            <div className={classes.join(" ")}>
                <header className="WizardTimelineCard__header">
                    <h3 className="WizardTimelineCard__title">
                        {adding ? "Add" : "Update"} Interaction
                    </h3>
                </header>

                <form
                    className="WizardTimelineCard__form"
                    onSubmit={this.handleSubmit}
                >
                    <div className="WizardTimelineCard__input">
                        <DropZone
                            onDrop={this.handleDropFile}
                            disabled={uploadDisabled}
                        >
                            <Textarea
                                className="form-control WizardTimelineCard__comment"
                                placeholder="Write a comment..."
                                value={this.state.comment}
                                onChange={this.handleChange}
                                onPaste={this.handlePaste}
                                autoFocus
                                maxLength={commentLength}
                                required={isTextRequired}
                            />
                            <div className="clearfix">
                                <FileSelector
                                    onSelect={this.handleSelectFile}
                                    accept=".png,.jpg,.gif, .webm, .mp4, .mp3, .wav"
                                    disabled={uploadDisabled}
                                    icon="camera"
                                    text="Photo"
                                />
                                 <MediaRecorderButton
                                    disabled={uploadDisabled}
                                    icon="video-camera"
                                    onRecorded={this.handleVideoRecorded}
                                    text="Record"
                                />
                            </div>
                        </DropZone>

                        <div className="WizardTimelineCard__commentLength">
                            {this.state.comment.length} / {commentLength}
                        </div>
                    </div>

                    <WizardTimelineCardPreview
                        metadata={this.state.metadata}
                        url={this.state.url}
                        image={this.state.image}
                        file={this.state.file}
                        loading={this.state.loadingPreview}
                        onRemove={this.handleRemovePreview}
                    />

                    <footer className="WizardTimelineCard__footer clearfix">
                        <div className="pull-left">
                            {this.renderSaveButton()}
                        </div>

                        <div className="pull-right">
                            <button
                                type="button"
                                onClick={this.props.onCancel}
                                className="WizardTimelineCard__cancel"
                            >
                                <i className="fa fa-remove" /> Cancel
                            </button>
                        </div>
                    </footer>
                </form>
            </div>
        );
    }

    renderSaveButton() {
        let content;

        if (this.state.saving) {
            content = (
                <span>
                    <AnimatedIconLoading /> Saving...
                </span>
            );
        } else {
            content = (
                <span>
                    <i className="fa fa-check" /> Save changes
                </span>
            );
        }

        return (
            <button
                className="WizardTimelineCard__submit"
                disabled={this.state.saving}
            >
                {content}
            </button>
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/components/TimelineCard/WizardTimelineCard/WizardTimelineCard.js